import './styles/StudentIntake.scss'
import queryString from 'query-string';
import FormButton from "./forms/FormButton";
import FormButtons from "./forms/FormButtons";
import FormField from "./fields/FormField";
import GenderField from "./fields/GenderField";
import GradeField from "./fields/GradeField";
import HouseholdField from "./fields/HouseholdIntakeField";
import NameField from "./fields/NameField";
import ParentField from "./fields/ParentIntakeField";
import Placeholder from 'components/Placeholder';
import RaceField from "./fields/RaceField";
import React, { useState } from 'react';
import SchoolGroupField from "./fields/SchoolGroupField";
import YearField from "./fields/YearField";
import YesNoField from "./fields/YesNoField";
import duckify, { dependentDuck, singleDuck } from "../../utils/duckify";
import { Form, Formik } from 'formik';
import { compose } from "redux";
import { mergeDefaults } from "../../utils/objects";
import { isBrandedPortal } from "./utils/schools";
import { withRouter } from "react-router";
import {
  sharedPortalHouseholds as households,
  sharedPortalIntakes as intakes,
  sharedPortalParents as parents,
  sharedPortalSchoolGroups,
  sharedPortalStudents as students,
  sharedPortalUsers,
  sharedPortalUsers as users
} from "../../ducks";

/**
 * Creates/updates a shared portal student, their parent relationships, and their household memberships.
 *
 * This component is used at account registration time to perform user intake, and from the My Profile page to edit
 * an existing student. It behaves differently for each use case, and that behavior is determined by the route,
 * whether `/intakes` or `/students`.
 */
const StudentIntake = ({ location, params, router, save, currentGroup, defaultGroup, intake = {}, user, duckMeta }) => {
  const [errors, setErrors] = useState({});
  const [nextPage, setNextPage] = useState();
  const { student = {} } = intake;
  const pageAndSearch = (params.page && params.page.split('?')) || []; // page ends up with search params after redirect
  const page = pageAndSearch[0];
  const intakeMeta = duckMeta.intake || { ready: true };
  const currentGroupMeta = duckMeta.currentGroup;
  const defaultGroupMeta = duckMeta.defaultGroup;
  const ready = intakeMeta.ready && currentGroupMeta.ready && defaultGroupMeta.ready;

  const search = pageAndSearch.length > 1 ? queryString.parse(`${pageAndSearch[1]}`) : queryString.parse(location.search);
  const searchParams = `?${queryString.stringify({ ...search })}`;

  // basePath will be either "/intakes" or "/students"
  const basePath = location.pathname.match(/\/\w+/)[0];
  const isIntake = basePath === '/intakes';

  let group;
  if (currentGroup && currentGroup.id === undefined) {
    group = defaultGroup;
  } else {
    group = currentGroup;
  }

  // Normally the user preference, which holds the default group selection, is included in the `intake` object
  // from the server. But before a student is created during the intake flow, there is no intake object. We
  // can manually include it in `initialValues` for it to correctly pre-populate on the form.
  const preferenceDefault = user.user_preference ? { user_preference: user.user_preference } : {};
  const initialValues = mergeDefaults(intake, preferenceDefault);

  // Get group-specific ethnicity and gender options
  const ethnicityOptions = group && group.ethnicity_options ? group.ethnicity_options.split(",") : null;
  const genderOptions = group && group.gender_options ? group.gender_options.split(",") : null;
  const genderHidden = group && group.gender_hidden;
  const genderOptional = group && group.gender_optional;
  const ethnicityHidden = group && group.ethnicity_hidden;
  const ethnicityOptional = group && group.ethnicity_optional;
  const householdHidden = group && group.household_hidden;
  const householdOptional = group && group.household_optional;

  const intakeUrl = (page, id = intake.id) => {
    if (page === 'DONE') {
      return isIntake ? `/explore_schools${searchParams}` : '/profile';
    } else {
      return `${basePath}/${page}${id ? `/${id}` : ''}${searchParams}`;
    }
  };

  const isHouseholdEditable = (householdId) => !intake.non_editable_household_ids.includes(householdId);

  const isParentEditable = (parentId) => !intake.non_editable_parent_ids.includes(parentId);

  const onSubmit = (values, { setValues, validateForm }) => {
    return save({ ...values, page, group_id: group && group.id }).then(response => {
      // load values from response into form
      const { entities, result } = response;
      const intakeId = result.intake;
      setValues(entities["intakes"][intakeId]);

      // rewrite browser url to include intake id
      if (!params.id && intakeId) {
        router.replace(intakeUrl(page, intakeId));
      }

      // route to next page
      let url = intakeUrl(nextPage, intake.id || intakeId);
      setNextPage(null);
      router.push(url);
    }).catch(response => {
      if (response.intake) {
        setErrors(response.intake.errors);
        validateForm(); // push errors to formik
      }
    });
  };

  return <React.Fragment>
    {!ready && (<Placeholder.text />)}

    {ready && intakeMeta.error && (
      <h2 className="error">Student Not Found</h2>
    )}

    {ready && !intakeMeta.error && (<Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={() => errors}>
      {
        ({ handleSubmit, values, validateForm, setValues }) => {
          const navigate = (nextPage, submit = true) => {
            setErrors({});

            if (submit) {
              setNextPage(nextPage);
            }

            return validateForm().then(() =>
              submit
                ? handleSubmit()
                : router.push(intakeUrl(nextPage))
            );
          };

          return (
            <Form className="sp-intake-form sp-form">
              <h1>Shared Admissions Portal</h1>
              {page === "location" && (
                <React.Fragment>

                  <h2>Location</h2>
                  <div className="row mb-3">
                    <SchoolGroupField
                      label="Select your primary school search area"
                      name="user_preference.default_shared_portal_group_id"
                      required
                    />
                  </div>

                  <FormButtons>
                    <FormButton onClick={() => navigate("student")}>Next</FormButton>
                  </FormButtons>
                </React.Fragment>
              )}

              {page === "student" && (
                <React.Fragment>
                  <h2>Student Information</h2>

                  {isIntake && (<p className="sp-intake-intro">
                    Please provide information about your student. This information will be used later to submit
                    applications to the schools of your choice.
                  </p>)}

                  <NameField entity="student" hideTitle required />

                  {!genderHidden && genderOptions && (
                    <GenderField entity="student" label="Gender" options={genderOptions} required={!genderOptional} />
                  )}

                  <div className="row">
                    <FormField label="Birth Date" type="date" name="student.birth_date" colWidth={6} showYearDropdown required />
                  </div>
                  <div className="row">
                    <GradeField name="student.current_grade" label="Current Grade" required />
                    <GradeField name="student.apply_grade" label="Interested in Grade" required />
                    <YearField name="student.apply_year" label="For the Fall of" student={student} required />
                  </div>
                  <div className="row">
                    <FormField label="Current School" type="text" name="student.current_school" colWidth={12} required />
                  </div>

                  {!ethnicityHidden && ethnicityOptions && (
                    <RaceField name="student.race" label="Race" options={ethnicityOptions} required={!ethnicityOptional} />
                  )}

                  <FormButtons>
                    {(isIntake && !isBrandedPortal(group, window.location.hostname) &&
                      <FormButton style="outline-primary" onClick={() => navigate("location", false)}>Previous</FormButton>)}
                    {(!isIntake &&
                      <FormButton style="outline-primary" onClick={() => router.push("/profile")}>Cancel</FormButton>)}
                    <FormButton onClick={() => navigate("household1")}>Next</FormButton>
                  </FormButtons>
                </React.Fragment>
              )}

              {page === "household1" && (
                <React.Fragment>
                  <h2>Household Information</h2>

                  {!householdHidden && (
                    <HouseholdField entity="household1" label="Household address" editable={isHouseholdEditable} required={!householdOptional} />
                  )}

                  <h2>Parent Information</h2>
                  <ParentField entity="parent1" label="Parent" editable={isParentEditable} required />

                  <div className="row">
                    <YesNoField name="is_parent2" label="Add another parent in this household?" />
                  </div>

                  {values.is_parent2 && (
                    <React.Fragment>
                      <h2>Additional Parent Information</h2>
                      <ParentField entity="parent2" label="Additional Parent" editable={isParentEditable} required />
                    </React.Fragment>
                  )}

                  <FormButtons>
                    <FormButton style="outline-primary" onClick={() => navigate("student", false)}>Previous</FormButton>
                    <FormButton onClick={() => navigate('household2')}>Next</FormButton>
                  </FormButtons>
                </React.Fragment>
              )}

              {page === "household2" && (
                <React.Fragment>
                  <h2>Secondary Household Information</h2>

                  <div className="row">
                    <YesNoField name="is_household2" label="Add a secondary household?" />
                  </div>

                  {values.is_household2 && (
                    <React.Fragment>
                      {!householdHidden && (
                        <HouseholdField entity="household2" label="Household address" editable={isHouseholdEditable} required={!householdOptional} />
                      )}

                      <h2>Parent Information</h2>
                      <ParentField entity="parent3" label="Parent" editable={isParentEditable} required />

                      <div className="row">
                        <YesNoField name="is_parent4" label="Add another parent in this household?" />
                      </div>

                      {values.is_parent4 && (
                        <React.Fragment>
                          <h2>Additional Parent Information</h2>
                          <ParentField entity="parent4" label="Additional parent" editable={isParentEditable} required />
                        </React.Fragment>
                      )}
                    </React.Fragment>
                  )}

                  <FormButtons>
                    <FormButton style="outline-primary" onClick={() => navigate("household1", false)}>Previous</FormButton>
                    <FormButton onClick={() => navigate("DONE")}>Done</FormButton>
                  </FormButtons>
                </React.Fragment>
              )}
            </Form>
          );
        }
      }
    </Formik>)}
  </React.Fragment>;
};

const mapDispatchToProps = (dispatch) => ({
  save: (intake) => dispatch(intakes.actions.save(intake)).then(
    (response) => {
      dispatch(students.actions.markDirty())
        .then(() => dispatch(households.actions.markDirty()))
        .then(() => dispatch(parents.actions.markDirty()))
        .then(() => dispatch(users.actions.markDirty()));
      return Promise.resolve(response);
    }
  )
});

const mapDucksToProps = ({ params }) => {
  const user = singleDuck(sharedPortalUsers, 'current');

  const currentGroup = singleDuck(sharedPortalSchoolGroups, 'current');

  const defaultGroup = dependentDuck(
    user,
    ({ value }) => {
      if (value.user_preference && value.user_preference.default_shared_portal_group_id) {
        return singleDuck(sharedPortalSchoolGroups, value.user_preference.default_shared_portal_group_id);
      } else {
        return undefined;
      }
    }
  )

  const intake = params.id ? { intake: singleDuck(intakes, params.id) } : {};

  return { user, currentGroup, defaultGroup, ...intake };
};

export default compose(
  withRouter,
  duckify(mapDucksToProps, undefined, mapDispatchToProps)
)(StudentIntake);
